import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Divider,
  Collapse,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import PlanOptionsCard, { PLAN_OPTION_TARGET } from './PlanOptionsCard';
import BasicPlanCard from './BasicPlanCard';
import PlanCard from './PlanCard';
import { usePieceEstimate } from '../hooks/usePieceEstimate';
import PaymentMethodChooser, { NEW_CARD } from './PaymentMethodChooser';
import RichMessage from './RichMessage';
import api from '../services/api';
import {
  derivePendingChange, pendingChangeMessage, PENDING_CHANGE_LABEL, scheduledDowngradeMessage,
  deriveCancellationBanner, cancellationScheduledMessage, CANCELLATION_SCHEDULED_LABEL,
  derivePlanActions, isLiveSubscription,
} from '../utils/subscriptionState';
import { getPlanDisplayName, getPlanRelationship, PLAN_RELATIONSHIP } from '../utils/planUtils';
import { CREDITS_PATH, PLANS_PATH } from '../utils/routes';
import { formatMoney, describeProration } from '../utils/money';
import {
  downgradeQuoteMessage, DOWNGRADE_SCHEDULED_TITLE, checkoutPreamble, creditPacksPrompt,
} from '../utils/subscriptionCopy';
import {
  buildQuoteBreakdown, creditHeadline, creditExplanation, formatQuoteDate, prorationBasisNote,
} from '../utils/upgradeQuote';
import { isInlineCardEntryAvailable } from '../services/stripeClient';
import { PLANS_WIDE_UP, APP_HEADER_HEIGHT } from '../utils/layout';

const toDisplayName = (entry) => getPlanDisplayName({ name: entry?.name, slug: entry?.slug });

/**
 * Where the in-progress plan choice is parked so a refresh does not lose it.
 *
 * BILL-002 requires that "the selected plan … survives the card-entry step. If
 * the user refreshes or navigates back, the flow recovers gracefully rather
 * than resetting to step one." sessionStorage rather than localStorage: a plan
 * selection is meaningful for this browsing session only, and resurrecting a
 * three-day-old intent to upgrade would be worse than forgetting it.
 */
const PENDING_KEY = 'improveseo.pendingPlanChange';

const readPendingSelection = (userId) => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Never restore another account's intent — sessions can change under a
    // shared browser, and re-opening a stranger's upgrade dialog is alarming.
    if (!parsed || parsed.userId !== userId) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const writePendingSelection = (userId, planId) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ userId, planId, at: Date.now() }));
  } catch (_) { /* private browsing; the flow still works, it just won't resume */ }
};

const clearPendingSelection = () => {
  try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
};

/**
 * A key that is stable for one confirm ACTION and changes for the next one.
 *
 * Reused across retries of the same submission so a double click, a slow
 * network and an impatient user all collapse to one charge; regenerated once
 * the action completes so a genuine second upgrade is not silently swallowed
 * by Stripe returning the first one's cached response.
 */
const newIdempotencyKey = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/**
 * How far below the top of the window the plans land when scrolled to: the
 * sticky header, plus room to breathe so the toggle does not sit flush under it.
 */
const PLANS_SCROLL_MARGIN = APP_HEADER_HEIGHT + 24;

const PlanSelector = ({
  userId, currentSubscription, onPlanChanged, onTabChange,
  /**
   * Opens the credit-pack picker — the same dialog the Credits page's Add
   * Credits button opens. Optional: the link is simply not rendered without it,
   * so a future caller that has no purchase flow mounted cannot produce a link
   * that opens nothing.
   */
  onBuyCreditPacks = null,
  /** False until the price table resolves; the link opens an empty shelf otherwise. */
  creditPacksReady = false,
  /**
   * Opens the cancel-subscription dialog — the same one the Credits page's
   * Cancel Subscription button opens, mounted by the parent so there is one
   * dialog and one handler behind both entry points.
   *
   * Optional, and on the same principle as onBuyCreditPacks: without it no
   * Cancel button is rendered, so a caller with no cancel flow mounted cannot
   * put a control on screen that does nothing.
   */
  onCancelPlan = null,
  /**
   * Opens the DOWNGRADE-to-Basic confirmation — the same dialog and handler as
   * onCancelPlan, worded for the Basic card's "Downgrade plan" button (see
   * downgradeToBasicDialog). Optional: without it that button falls back to
   * onCancelPlan, so a caller that only knows about cancelling still works.
   */
  onDowngradeToBasic = null,
  // What is on sale, with real prices — the Basic card quotes them, so they
  // come from the same payload the pack picker and the checkout use.
  packages = [],
  /**
   * Where to scroll once the grid exists, for callers that arrive with the
   * choice already made — both "Get subscription" buttons (the after-the-trial
   * dialog's and the Basic plan card's), which land here via PLANS_SCROLLED_PATH.
   * OLD: "currently "Get subscription" in the after-the-trial dialog" — the
   * Basic card's button joined it by request.
   *
   * A PLAN_OPTION_TARGET value, so the one handler serves both this and the
   * options card's links; anything else is ignored. Null (the default) leaves
   * the page at the top, which is right for everyone who has not already
   * decided.
   */
  scrollTo = null,
  /**
   * The account is on Basic: no subscription row, and the trial behind it has
   * ended. The parent's `trialExpired`, passed rather than re-derived — nothing
   * in currentSubscription can tell a Basic account from a running trial, since
   * neither has a row.
   */
  onBasicPlan = false,
}) => {
  const [plans, setPlans] = useState([]);
  // Monthly or yearly. Initialised from what the customer is ALREADY billed on
  // (the server tells us), so an annual subscriber does not open the page on
  // monthly prices and conclude their plan changed.
  const [billingInterval, setBillingInterval] = useState('month');
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState('Error');
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  // "You have already scheduled this" — shown instead of a second quote when
  // the customer picks the plan their downgrade is already queued for.
  const [scheduledNoticeOpen, setScheduledNoticeOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Shared with the Credits page and Settings — see derivePendingChange.
  // Declared up here, above handleSelectPlan, because that handler reads it to
  // decide whether a click is a real plan change or a repeat of one already
  // scheduled.
  const pendingChange = derivePendingChange(currentSubscription);
  // BILL-003 — the prorated quote shown in the confirm dialog, and the
  // proration_date that makes it binding on commit.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // ── BILL-002: payment methods, collected inside this flow ────────────────
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pmLoading, setPmLoading] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const cardRef = useRef(null);
  // Scroll targets for the plan-options card at the top. The card names a
  // destination; this decides where that is, so the copy does not have to know
  // the layout and the layout does not have to know the copy.
  const plansRef = useRef(null);
  const basicPlanRef = useRef(null);

  const handleGoTo = (destination) => {
    const toBasicPlan = destination === PLAN_OPTION_TARGET.BASIC_PLAN;
    const node = toBasicPlan ? basicPlanRef.current : plansRef.current;
    // Optional-chained: the pay-as-you-go card is not rendered when the host
    // passes no handler for it, and a link to nothing should do nothing rather
    // than throw.
    node?.scrollIntoView({
      behavior: 'smooth',
      // 'start' for both. It is measured from the TOP OF THE WINDOW, where the
      // Dashboard's sticky header sits — so each target carries a
      // scroll-margin-top (see PLANS_SCROLL_MARGIN) that lands it just below
      // the header: the Monthly/Yearly toggle for the plans, the card's top
      // edge for Basic.
      //
      // REQUESTED (QA): "[See pay-as-you-go option] … view should scroll so
      // the top of the card is visible". It was 'center', which puts the
      // card's MIDDLE mid-screen — on a short window, or with the card laid
      // out tall, that leaves its top under the header or above the fold.
      // OLD: block: toBasicPlan ? 'center' : 'start',
      block: 'start',
    });
  };

  // ── The scroll a caller asked for on the way in ───────────────────────────
  // Waits for `loading` to clear, because the target is the plan grid and the
  // grid does not exist while the request is in flight — scrolling on mount
  // would find a spinner and land on nothing.
  //
  // The ref makes it fire ONCE per arrival. Without it every re-render that
  // followed (a toggle flip, a dialog opening) would yank the page back down,
  // which reads as the screen fighting the user.
  const didAutoScrollRef = useRef(false);
  useEffect(() => {
    if (loading || !scrollTo || didAutoScrollRef.current) return;
    didAutoScrollRef.current = true;
    handleGoTo(scrollTo);
    // handleGoTo is stable for this purpose: it only reads refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, scrollTo]);

  // One key per confirm action (see newIdempotencyKey).
  const idempotencyKeyRef = useRef(null);

  // Set while we are opening Stripe's hosted checkout and the browser is on its
  // way there. Keeps the plan grid inert so a second click cannot start a
  // second checkout in the seconds before the page unloads.
  const [redirecting, setRedirecting] = useState(false);

  // The plan awaiting confirmation before we hand the browser to Stripe.
  // Null when no first-subscription checkout is pending. See checkoutPreamble.
  const [checkoutIntent, setCheckoutIntent] = useState(null);
  // "about N pieces" in the checkout dialog, from GET /credits/estimate.
  const checkoutPieces = usePieceEstimate(checkoutIntent?.content_credits ?? null);

  // ── Billing address: mandatory before any plan purchase ──────────────────
  // `resume` records what the block interrupted, so saving the address carries
  // the user onward instead of dropping them back on the plan grid.
  // Set once the user has saved an address inside this flow. Stops a slow or
  // lagging read from bouncing them back into the gate they just cleared.

  const inlineCardsAvailable = isInlineCardEntryAvailable();
  const usingNewCard = selectedPmId === NEW_CARD;

  /**
   * Is this user CHANGING a subscription, or STARTING one?
   *
   * One predicate, used by every decision in this file that turns on it — which
   * route the confirm takes (change-plan vs subscribe-to-plan) and whether a
   * user with no card is eligible for hosted checkout. They have to agree: a
   * hosted checkout CREATES a subscription, so running it for someone who
   * already has one would leave them holding two.
   *
   * ── A SUBSCRIPTION THAT HAS ENDED IS NOT ONE ──────────────────────────────
   * The row outlives the subscription. When Stripe deletes it the server marks
   * the row 'cancelled' and keeps its plan_id, and /plans still flags that plan
   * is_current. Reading "has a row with a plan" as "is paying" therefore put a
   * Basic account that USED to subscribe on the change-plan path — which the
   * server refuses ("No active subscription") — and drew its old plan as its
   * Current Plan, with the cheaper one as a Downgrade.
   *
   * Live status is required now, from the same isLiveSubscription the Basic
   * card and the credit-pack prompt below already read. Such an account is
   * starting a subscription again, and every surface here says so: the plan
   * buttons read Subscribe, the dialog is headed Subscribe, and the confirm
   * goes to subscribe-to-plan or checkout — both of which the server accepts
   * for a cancelled row.
   *
   * OLD: const hasPaidSubscription = Boolean(currentSubscription && currentSubscription.plan_id);
   */
  const hasPaidSubscription = isLiveSubscription(currentSubscription)
    && Boolean(currentSubscription.plan_id);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchPlans = async () => {
    if (!userId) {
      console.log('No userId provided, skipping plan fetch');
      setLoading(false);
      setErrorDialogTitle('User Not Found');
      setErrorDialogMessage('User information not available. Please refresh the page.');
      setErrorDialogOpen(true);
      return;
    }

    try {
      setLoading(true);
      const data = await api.getSubscriptionPlans(userId);
      setPlans((data.plans || []).filter(p => p.slug !== 'free' && p.price_cents > 0));
      if (data.current_interval === 'year') setBillingInterval('year');
    } catch (err) {
      console.error('Error fetching plans:', err);
      setErrorDialogTitle('Failed to Load Plans');
      setErrorDialogMessage(`Unable to load subscription plans: ${err.response?.data?.error || err.message}`);
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // BILL-003 — price the change BEFORE the user commits. The dialog used to
  // announce the full monthly plan price, which is not what a mid-cycle upgrade
  // charges: Stripe prorates it. The real figure only appeared afterwards, in
  // the success message — i.e. after the card had been charged.
  const loadProration = useCallback(async (plan) => {
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const result = await api.previewPlanChange(userId, plan.id);
      setPreview(result?.data || null);
      if (!result?.data) {
        setPreviewError('We could not work out what this change will cost.');
      }
    } catch (err) {
      // BILL-003 — a failed quote BLOCKS the change. Confirming a charge whose
      // amount we could not retrieve is precisely what this ticket forbids, so
      // there is no "carry on with generic copy" path any more.
      setPreviewError(
        err.response?.data?.error
        || 'We could not work out what this change will cost. Please try again in a moment.',
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [userId]);

  /**
   * Load the saved cards for the selector, and preselect the default.
   *
   * BILL-002 requires the default to be preselected and a new card to be
   * addable without leaving. Also acts as the cache invalidation BILL-001 asks
   * for: it is re-run after every payment-method mutation rather than trusting
   * a list fetched when the dialog opened.
   */
  const loadPaymentMethods = useCallback(async () => {
    if (!userId) return [];
    setPmLoading(true);
    try {
      const res = await api.getPaymentMethods(userId);
      const methods = res?.payment_methods || [];
      setPaymentMethods(methods);
      const preferred = methods.find((m) => m.is_default) || methods[0];
      // With no saved card there is nothing to choose between: go straight to
      // the card form rather than showing an empty radio group.
      setSelectedPmId(preferred ? preferred.id : NEW_CARD);
      return methods;
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      setPaymentMethods([]);
      setSelectedPmId(NEW_CARD);
      return [];
    } finally {
      setPmLoading(false);
    }
  }, [userId]);

  /**
   * Ask the server for a SetupIntent so Elements can confirm a card in page.
   *
   * Fetched lazily — only when a new card is actually going to be entered — so
   * we do not create an abandoned SetupIntent every time someone opens the
   * dialog to look at the price.
   */
  const ensureSetupIntent = useCallback(async () => {
    if (setupClientSecret) return setupClientSecret;
    try {
      const res = await api.createSetupIntent(userId);
      const secret = res?.data?.client_secret || null;
      setSetupClientSecret(secret);
      if (!secret) setCardError('We could not open the card form. Please try again.');
      return secret;
    } catch (err) {
      setCardError(
        err.response?.data?.error || 'We could not open the card form. Please try again.',
      );
      return null;
    }
  }, [userId, setupClientSecret]);

  useEffect(() => {
    if (confirmDialogOpen && usingNewCard && inlineCardsAvailable) ensureSetupIntent();
  }, [confirmDialogOpen, usingNewCard, inlineCardsAvailable, ensureSetupIntent]);

  /**
   * @param methodsLoaded true when the caller has ALREADY awaited
   *   loadPaymentMethods, so the dialog does not fetch the same list twice.
   */
  const openConfirmFor = useCallback((plan, { methodsLoaded = false } = {}) => {
    setSelectedPlan(plan);
    setConfirmDialogOpen(true);
    setCardError(null);
    setCardComplete(false);
    idempotencyKeyRef.current = newIdempotencyKey();
    writePendingSelection(userId, plan.id);
    loadProration(plan);
    if (!methodsLoaded) loadPaymentMethods();
  }, [userId, loadProration, loadPaymentMethods]);

  /**
   * Send the user straight to Stripe's hosted checkout for this plan.
   *
   * Returns what happened, because the caller has to react differently to each:
   *   'redirecting'      — the browser is navigating away; do nothing more.
   *   'address_required' — the server's address rule fired; collect it.
   *   'unavailable'      — fall back to the in-page flow rather than dead-end.
   */
  const startHostedCheckout = useCallback(async (plan) => {
    try {
      const res = await api.createSubscriptionCheckout(userId, plan.id, {
        returnTo: PLANS_PATH,
        interval: billingInterval,
        idempotencyKey: newIdempotencyKey(),
      });
      const url = res?.data?.url;
      if (!url) return 'unavailable';
      // Nothing is parked for recovery: the user is leaving for Stripe, and
      // coming back to a dialog they never opened is not "recovering" anything.
      clearPendingSelection();
      window.location.assign(url);
      return 'redirecting';
    } catch (err) {
      console.error('Could not start checkout:', err);
      return 'unavailable';
    }
  }, [userId, billingInterval]);

  /**
   * The single entry point for "the user picked this plan".
   *
   * ── Client decision: no saved card means checkout, not "add a card" ────────
   * A user with nothing on file used to be shown a card form whose only purpose
   * was to save a payment method so that a SECOND request could then charge it.
   * That is two payment UIs and two round trips to buy one thing, and it leaves
   * an orphaned card behind whenever the user abandons the second step. They now
   * go straight to Stripe's hosted checkout, which collects the card, takes the
   * first payment and creates the subscription in one page — and gets 3DS/SCA,
   * wallets and local payment methods for free.
   *
   * Only for a FIRST subscription. With one already running, the same hosted
   * page would create a second — a plan CHANGE has to prorate against the
   * existing subscription, so it stays on the in-page confirm dialog.
   */
  const beginPlanChange = useCallback(async (plan) => {
    if (hasPaidSubscription) {
      openConfirmFor(plan);
      return;
    }

    setRedirecting(true);
    const methods = await loadPaymentMethods();

    if (methods.length === 0) {
      // ── Say what is being bought BEFORE leaving for Stripe ──────────────
      // This path used to redirect immediately, so the first thing a customer
      // saw after clicking Grow was Stripe's page reading "Subscribe to
      // ImproveSEO Subscription" — every plan shares one Stripe Product, so it
      // names no plan and mentions none of the credits being bought.
      //
      // That page is not ours to change: Stripe renders the Product, and a
      // Checkout session cannot override a recurring Price's product name. Until
      // each plan has its own Product, this states the same facts in our words,
      // at the moment of the decision rather than after it.
      setRedirecting(false);
      setCheckoutIntent(plan);
      return;
    }

    setRedirecting(false);
    openConfirmFor(plan, { methodsLoaded: true });
  }, [hasPaidSubscription, loadPaymentMethods, openConfirmFor]);

  /** Confirmed the preamble — hand the browser to Stripe. */
  const proceedToHostedCheckout = useCallback(async () => {
    const plan = checkoutIntent;
    if (!plan) return;
    setRedirecting(true);

    const outcome = await startHostedCheckout(plan);
    // Leave the grid inert: the page is unloading.
    if (outcome === 'redirecting') return;

    setCheckoutIntent(null);
    setRedirecting(false);

    // 'unavailable' — checkout could not be opened (endpoint not deployed yet,
    // provider outage). The in-page card flow can still complete the purchase,
    // so fall through to it rather than stranding the user.
    openConfirmFor(plan, { methodsLoaded: true });
  }, [checkoutIntent, openConfirmFor, startHostedCheckout]);

  // BILL-002 — recover the flow after a refresh or a back-navigation instead of
  // dumping the user back on the plan grid with no memory of what they chose.
  useEffect(() => {
    if (!userId || plans.length === 0 || confirmDialogOpen || selectedPlan) return;
    const pending = readPendingSelection(userId);
    if (!pending) return;
    const plan = plans.find((p) => p.id === pending.planId);
    if (plan) openConfirmFor(plan);
    else clearPendingSelection();
  }, [userId, plans, confirmDialogOpen, selectedPlan, openConfirmFor]);

  const handleSelectPlan = async (plan) => {
    // Prevent a second dialog — or a second checkout — opening on top of one
    // that is already in flight.
    if (processing || redirecting) return;

    // ── Already scheduled to move to THIS plan ──────────────────────────────
    // Before anything else, and deliberately before the quote. Downgrading a
    // second time to the plan already queued used to be accepted end to end:
    // previewPlanChange wrote a fresh billing_disclosures row, /change-plan
    // re-sent the same price to Stripe, and the customer was congratulated on
    // scheduling what was already scheduled. None of that told them the one
    // thing they needed to know, which is that it is done.
    //
    // Returning here means no quote is fetched, so no disclosure row is burned
    // — the reason this sits above the address gate rather than below it.
    if (pendingChange.visible && pendingChange.planId === plan.id) {
      setScheduledNoticeOpen(true);
      return;
    }

    await beginPlanChange(plan);
  };

  /**
   * The single submit action.
   *
   * Card confirmation and the plan change happen in ONE user gesture, which is
   * the substance of BILL-002: previously the user was pushed to Settings to
   * add a card and had to start the upgrade again from the plan grid.
   */
  const handleConfirmChange = async () => {
    if (!selectedPlan || processing) return;

    setProcessing(true);
    setCardError(null);

    try {
      // ── Step 1: make sure there is a card, and that it is the default ────
      let paymentMethodId = usingNewCard ? null : selectedPmId;

      if (usingNewCard) {
        if (!inlineCardsAvailable) {
          // No publishable key configured, so there is no in-page card entry to
          // fall back on — this has to become a redirect.
          //
          // For a FIRST subscription that redirect is checkout, not card setup:
          // sending someone off to save a card and then bringing them back to
          // press confirm again is the round trip this flow exists to remove.
          if (!hasPaidSubscription && await startHostedCheckout(selectedPlan) === 'redirecting') return;

          // Changing an existing plan still needs a card SAVED first — the
          // change itself is a proration against the running subscription, not
          // a new checkout. Returns to this exact screen; the pending selection
          // means the dialog reopens on the selected plan when they get back.
          const result = await api.createSetupSession(userId, PLANS_PATH);
          if (result?.url) {
            window.location.href = result.url;
            return;
          }
          setCardError('We could not open the card form. Please add a card from Settings.');
          return;
        }

        const outcome = await cardRef.current?.confirm();
        if (!outcome || outcome.error) {
          // BILL-002 — a decline keeps the user HERE, with the plan still
          // selected and the form still mounted, and it does not consume the
          // idempotency key: the retry is the same action.
          setCardError(outcome?.error || 'That card could not be saved. Try another card.');
          return;
        }
        paymentMethodId = outcome.paymentMethodId;
      }

      // Reuses the BILL-001 endpoint rather than duplicating the promotion
      // logic: it writes our record and Stripe's together, with the ownership
      // check and the subscription re-point.
      if (paymentMethodId) {
        await api.setDefaultPaymentMethod(userId, paymentMethodId);
        // Cache invalidation (BILL-001): the list the dialog is showing is now
        // stale in at least its is_default flags.
        await loadPaymentMethods();
      }

      // ── Step 2: the plan change itself ───────────────────────────────────
      const idempotencyKey = idempotencyKeyRef.current;

      const result = hasPaidSubscription
        ? await api.changePlan(userId, selectedPlan.id, preview?.proration_date || null, {
          idempotencyKey,
          disclosureId: preview?.disclosure_id || null,
        })
        : await api.subscribeToPlan(userId, selectedPlan.id, idempotencyKey, billingInterval);

      // The action completed: the next confirm is a genuinely new operation.
      idempotencyKeyRef.current = newIdempotencyKey();
      clearPendingSelection();

      setConfirmDialogOpen(false);
      setSelectedPlan(null);
      setSetupClientSecret(null);

      // ── One confirmation for a first subscription ──────────────────────────
      // This opened its own plain "Subscription active" dialog AND handed the
      // result to onPlanChanged, whose SuccessDialog opened too — two stacked
      // modals, neither looking like the other plan-change confirmations.
      // The parent now confirms it, in the same "Subscription active" dialog a
      // subscription started on Stripe's page gets; the flag says which case
      // this is. The local dialog stays only for a caller with no handler.
      // OLD: if (!hasPaidSubscription) setSuccessDialogOpen(true);
      // OLD: if (onPlanChanged) onPlanChanged(result);
      if (onPlanChanged) {
        onPlanChanged({ ...result, first_subscription: !hasPaidSubscription });
      } else if (!hasPaidSubscription) {
        setSuccessDialogOpen(true);
      }
      await fetchPlans();

    } catch (err) {
      console.error('Error changing plan:', err);
      const data = err.response?.data;

      // BILL-002 — anything the user can fix by trying another card stays in
      // the dialog. Only genuine faults escalate to the modal error dialog,
      // which is a dead end by nature.
      if (data?.requires_payment_method || err.response?.status === 402) {
        setCardError(data?.error || 'That payment could not be completed. Try another card.');
        // Force a fresh SetupIntent so the retry confirms cleanly rather than
        // against an intent Stripe already consumed.
        setSetupClientSecret(null);
        setSelectedPmId(NEW_CARD);
        return;
      }

      if (err.response?.status >= 500) {
        setErrorDialogTitle('Something went wrong');
        setErrorDialogMessage('We could not complete the change, and nothing was charged. Please try again in a moment.');
      } else if (err.isTimeout) {
        setErrorDialogTitle('That took too long');
        setErrorDialogMessage('The request timed out. Check your connection and try again — if the change did go through, you will see it on the Credits page.');
      } else if (!err.response || err.isNetworkError) {
        setErrorDialogTitle('No connection');
        setErrorDialogMessage('We could not reach the server. Check your internet connection and try again.');
      } else {
        setErrorDialogTitle('Error');
        setErrorDialogMessage(data?.error || 'We could not change your plan. Please try again.');
      }
      setConfirmDialogOpen(false);
      setErrorDialogOpen(true);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelDialog = () => {
    if (processing) return;
    setConfirmDialogOpen(false);
    setSelectedPlan(null);
    // Discard the quote — reopening must re-price rather than reuse a stale
    // proration_date, which Stripe would reject or price differently.
    setPreview(null);
    setPreviewError(null);
    setSetupClientSecret(null);
    setCardError(null);
    clearPendingSelection();
  };

  // BILL-001 link audit — these used to navigate by NUMERIC TAB INDEX, which
  // silently rotted when tabs were hidden or renamed: `onTabChange(4)` meant
  // Settings when Analytics was visible, then Support once it was hidden (what
  // the client reported), then Quick Start Guide after the Tutorial rename.
  // They now pass stable paths from utils/routes.js.
  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    if (onTabChange) onTabChange(CREDITS_PATH);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setSelectedPlan(null);
  };

  // The paid plan the user is actually on, or null on free/trial.
  //
  // `plans` excludes the free plan (filtered in fetchPlans), so a free-tier or
  // trial user resolves to null — which is right: with no paid plan to compare
  // against, every card is an upgrade and none can be a downgrade.
  //
  // Gated on hasPaidSubscription: once a subscription has ENDED, its plan is
  // still is_current in the /plans payload (see hasPaidSubscription above), and
  // honouring the flag drew a Basic account's old plan as "Current Plan" — not
  // buyable — with the cheaper one as a "Downgrade".
  //
  // OLD:
  //   const activePlan = plans.find((p) => p.is_current === true)
  //     || plans.find((p) => currentSubscription?.plan_id && p.id === currentSubscription.plan_id)
  //     || null;
  //   const currentPlan = plans.find((p) => p.is_current) || activePlan;
  const activePlan = hasPaidSubscription
    ? (plans.find((p) => p.is_current === true)
      || plans.find((p) => p.id === currentSubscription.plan_id)
      || null)
    : null;

  const currentPlan = hasPaidSubscription
    ? (plans.find((p) => p.is_current) || activePlan)
    : null;

  // This screen used to say nothing about a scheduled cancellation, so someone
  // arriving here from the Credits banner saw their plan sitting in the grid
  // looking perfectly normal — the one screen where "which plan am I on?" is
  // the whole question was the one screen that did not mention it was ending.
  const cancellationBanner = deriveCancellationBanner(currentSubscription);

  // Whether to offer Cancel under the current plan's card. Read from the SAME
  // helper the Credits page uses for its button row rather than a second rule
  // written here, so the two screens cannot disagree about whether cancelling
  // is on the table. It answers no in the two states where the control would
  // be wrong: no live paid plan (a trial or the free tier has nothing to
  // cancel), and a cancellation already scheduled — where the offer is to
  // reactivate, not to cancel a second time.
  const canCancel = onCancelPlan
    && derivePlanActions(currentSubscription).some((a) => a.key === 'cancel');

  // ── What the confirm button says and whether it can be pressed ───────────
  // BILL-003: the amount goes in the label, and the button is dead until we
  // have a figure we are prepared to stand behind.
  const amountDue = preview ? Number(preview.amount_due_cents ?? 0) : null;
  const quoteReady = Boolean(preview) && !previewLoading && !previewError;

  // A change that lands at the period end: nothing is charged today and the
  // existing card keeps working. Read once here, because the title, the confirm
  // button, the card block and the dialog body all key off it.
  const scheduledDowngrade = quoteReady && preview.effective === 'period_end';

  // No card is needed to SCHEDULE a change — and the card UI is hidden for one
  // (see the dialog body). Without this exemption the Confirm button stayed
  // disabled forever for anyone without a saved payment method, with the
  // explanation hidden along with the chooser: a dead end with no visible cause.
  const cardReady = scheduledDowngrade
    ? true
    : (!usingNewCard
      ? Boolean(selectedPmId)
      : (inlineCardsAvailable ? cardComplete : true));
  const canConfirm = quoteReady && cardReady && !processing && !pmLoading;

  const confirmLabel = () => {
    if (processing) return 'Working…';
    if (!quoteReady) return 'Confirm';
    // Named before the money cases: a downgrade is always $0 today, so it would
    // otherwise fall through to "Confirm — no charge today", which describes the
    // price rather than the decision being made.
    if (scheduledDowngrade) return 'Confirm downgrade';
    if (amountDue > 0) return `Confirm and pay ${formatMoney(amountDue, preview.currency)}`;
    if (amountDue < 0) return 'Confirm change';
    return 'Confirm — no charge today';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* "Pricing Options", not "Subscription Options": the page has not been
          only about subscriptions since Basic joined the grid. A pay-as-you-go
          account arriving here to buy a credit pack was reading a heading that
          named the one thing it was not doing. */}
      <Typography variant="h5" gutterBottom fontWeight="bold" mb={3}>
        Pricing Options
      </Typography>

      {/* Derived, so the plan grid cannot advertise a scheduled change that the
          Credits page has already (correctly) stopped showing.
          OLD: {currentSubscription?.pending_plan_id && ( ... )} */}
      {pendingChange.visible && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {/* Was "Your subscription changes on 9/19/2026" — no plan name (the
              most useful fact) and a locale-default date that reads as 9 Sep to
              a British user. Now identical to the Credits page, from one
              formatter. */}
          <strong>{PENDING_CHANGE_LABEL}</strong>{' '}
          {pendingChangeMessage(pendingChange, { planDisplayName: (name) => getPlanDisplayName({ name }) })}
        </Alert>
      )}

      {/* Same label, same sentence and same severity as the Credits page, from
          the one formatter. The two notices cannot both appear —
          derivePendingChange stands down while a cancellation is scheduled,
          because the change queued for period end never runs. */}
      {cancellationBanner.visible && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{CANCELLATION_SCHEDULED_LABEL}</strong>{' '}
          {cancellationScheduledMessage(cancellationBanner, {
            planName: currentSubscription?.plan_name,
            planDisplayName: (name) => getPlanDisplayName({ name }),
          })}
        </Alert>
      )}

      {/* The redirect is a full page navigation, so the last thing the user
          sees on our side has to explain itself — otherwise a slow round trip
          reads as a dead button. aria-live so it is announced, not just shown. */}
      {redirecting && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 3 }}
          aria-live="polite"
        >
          Taking you to our secure checkout…
        </Alert>
      )}

      <PlanOptionsCard onGoTo={handleGoTo} />

      {/* ── Where "See subscription options" and "Get subscription" land ──
          REQUESTED: the Monthly/Yearly toggle at the top of the window with the
          three plans under it. The grid alone used to be the target, which put
          the toggle above the fold and — because the window scrolls beneath a
          sticky header — the tops of the cards under that header. One target
          around both, with a margin that clears the header. */}
      <Box
        ref={plansRef}
        data-testid="plans-scroll-target"
        sx={{ scrollMarginTop: `${PLANS_SCROLL_MARGIN}px` }}
      >
        {/* ── Monthly / yearly ────────────────────────────────────────────────
            A two-button group rather than a switch: a switch has an implied
            "off" state, and neither billing interval is the absence of the
            other. Both options are labelled and always visible, so the saving is
            discoverable without toggling to find it. */}
        {plans.some((p) => p.yearly_available) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              role="group"
              aria-label="Billing interval"
              sx={{ display: 'inline-flex', bgcolor: '#F2F4F4', borderRadius: '999px', p: 0.5 }}
            >
              {[
                { key: 'month', label: 'Monthly' },
                { key: 'year', label: 'Yearly' },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  onClick={() => setBillingInterval(opt.key)}
                  aria-pressed={billingInterval === opt.key}
                  disableElevation
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 14,
                    px: 2.5,
                    py: 0.75,
                    minWidth: 104,
                    color: billingInterval === opt.key ? '#FFFFFF' : '#5A5F55',
                    bgcolor: billingInterval === opt.key ? '#1C7C73' : 'transparent',
                    '&:hover': { bgcolor: billingInterval === opt.key ? '#156874' : 'rgba(0,0,0,0.04)' },
                  }}
                >
                  {opt.label}
                  {opt.key === 'year' && (
                    <Box component="span" sx={{ ml: 0.75, fontSize: 11, fontWeight: 800, opacity: 0.9 }}>
                      2 MONTHS FREE
                    </Box>
                  )}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {/* ── The plan grid ──────────────────────────────────────────────────
            REQUESTED, and the whole of the requested layout in one declaration:
            three equal columns for the paid plans from 768px up, one column
            below it, and Basic spanning the full width beneath them.

            ── CSS grid, not <Grid container> ───────────────────────────────────
            MUI's Grid sizes items by theme breakpoint props, and the spec's
            breakpoint is 768px — between MUI's sm (600) and md (900). `md={4}`
            left a 800px tablet stacked when the spec says it should show three
            across, and `sm={4}` would have crushed three plan cards into 600px.
            See utils/layout.js for why the theme is not widened instead.

            It also removes the empty side space the request calls out. Grid sized
            the wrapped Basic card at one third and left the other two thirds of
            its row blank; `gridColumn: 1 / -1` below makes that row the full
            container width, with no margins to trim.

            `minmax(0, 1fr)` rather than `1fr`: a long feature label has a large
            min-content width, and plain `1fr` lets a column grow past its share
            to fit one — which is exactly how three "equal" columns end up
            unequal.

            alignItems defaults to stretch here, which is what aligns the three:
            PlanCard is a flex column whose CardContent has flexGrow:1, so the
            extra height lands in the feature list and every card's button is
            pushed to its bottom edge. Tops line up, bottoms line up, and the
            three CTAs sit on one line. */}
        {/* OLD: ref={plansRef} was on this grid — see the wrapper above. */}
        <Box
          data-testid="plan-grid"
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: '1fr',
            pt: 1,
            [PLANS_WIDE_UP]: {
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {plans.map((plan) => {
            // Computed ONCE and used for both the card's styling and the cancel
            // gate below. They must be the same answer: an earlier version gated
            // the button on `plan.is_current === true` alone, and that is not
            // what makes a card read "Current Plan" — getPlanRelationship also
            // matches on id, precisely because `is_current` can be absent or
            // stale in the payload. On an account where the server omits the
            // flag, the card said Current Plan and the Cancel button underneath
            // it never rendered.
            //
            // With no paid plan running (a trial, or Basic) every card is a plan
            // to subscribe to. getPlanRelationship is not consulted then: it
            // honours `is_current` first, and on a subscription that has ENDED
            // that flag still marks the old plan (see hasPaidSubscription).
            //
            // OLD: const relationship = getPlanRelationship(plan, activePlan);
            const relationship = hasPaidSubscription
              ? getPlanRelationship(plan, activePlan)
              : PLAN_RELATIONSHIP.UPGRADE;
            const isCurrent = relationship === PLAN_RELATIONSHIP.CURRENT;

            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                // Strict boolean compare: a stray truthy non-boolean from the API
                // must not mark a plan "current" and disable its button. And only
                // while that plan is actually running — see hasPaidSubscription.
                // OLD: isCurrentPlan={plan.is_current === true}
                isCurrentPlan={hasPaidSubscription && plan.is_current === true}
                relationship={relationship}
                // A trial or Basic account STARTS a subscription here, so the
                // buttons read Subscribe rather than Upgrade.
                subscribing={!hasPaidSubscription}
                isPending={pendingChange.visible && pendingChange.planId === plan.id}
                onSelectPlan={handleSelectPlan}
                interval={billingInterval}
                disabled={processing || redirecting}
                // Only the card the user is on gets it, so the button appears
                // once on the screen rather than under all three.
                onCancelPlan={canCancel && isCurrent ? onCancelPlan : undefined}
              />
            );
          })}

          {/* ── Basic: a row of its own beneath the three ──────────────────────
              REQUESTED: below the paid plans, horizontally — not a fourth column,
              and not a third of a row with two thirds of empty space beside it,
              which is what it was.

              Full width, and SHORT. REQUESTED after a brief narrower version
              (centred and capped at 760px): keep it spanning the row, but not
              tall. The height is BasicPlanCard's job — across this width it lays
              its features out in columns beside or beneath the name and buttons.

              `1 / -1` spans every column the grid has, so the row is exactly as
              wide as the three cards above it are together, gutters included.
              Below 768px the grid is one column and the span is a no-op.

              EXTRA TOP MARGIN, on top of the grid's 24px gap: the row above is as
              tall as Optimize, and Optimize is the popular card — PlanCard draws
              it at scale(1.03) from its TOP edge, so roughly 15-20px of it
              renders BELOW the box the grid measured, under a 32px shadow. Both
              land in that gap. Only from 768px up, because below it there is no
              card above this one to clear and the extra space would read as Basic
              having been cut adrift from the set.

              It is also the scroll target for "See pay-as-you-go option" in the
              options card at the top — the ref moved here from the text block
              that used to stand in for this card.

              REQUESTED (QA): "a little more space above the 4th column (Basic
              plan), it's too close to the Optimize column". Measured in Chrome
              at 1440×900: Optimize's scale overhangs its box by ~28px, so mt 2
              (16px) left about 12px between its bottom edge and this card; mt 5
              (40px) leaves 36px — clear of the overhang, and wider than the gap
              between the three plans, so Basic reads as its own row.

              scrollMarginTop: the scroll lands this card's top edge just below
              the sticky header — see handleGoTo. */}
          <Box
            ref={basicPlanRef}
            data-testid="basic-plan-scroll-target"
            // OLD: sx={{ [PLANS_WIDE_UP]: { gridColumn: '1 / -1', mt: 2 } }}
            sx={{
              scrollMarginTop: `${PLANS_SCROLL_MARGIN}px`,
              [PLANS_WIDE_UP]: { gridColumn: '1 / -1', mt: 5 },
            }}
          >
            <BasicPlanCard
              // REQUESTED: the wide row. Only the arrangement differs — see the
              // `layout` prop's note for why every piece inside is shared.
              layout="horizontal"
              packages={packages}
              creditPacksReady={creditPacksReady}
              onBuyCreditPacks={onBuyCreditPacks}
              // Downgrading to Basic IS ending the paid subscription — access
              // runs to the period end and the account then falls back here. So
              // it takes the existing, tested cancel path rather than a second
              // route to the same place. canCancel already answers "is there a
              // live paid plan with no cancellation queued", which is exactly
              // when Downgrade is the honest label.
              // REQUESTED: its own "Downgrade to the Basic plan" wording, so the
              // button that says Downgrade no longer opens a dialog headed
              // Cancel Subscription. Same handler behind both.
              // OLD: onDowngrade={canCancel ? onCancelPlan : undefined}
              onDowngrade={canCancel ? (onDowngradeToBasic || onCancelPlan) : undefined}
              hasPaidSubscription={Boolean(canCancel)}
              // Basic is active either way an account gets there: a trial that
              // ended with no subscription (onBasicPlan — the common case, which
              // the row check alone missed and labelled "Default plan"), or a
              // paid plan that has since ended.
              // OLD: isBasicActive={!isLiveSubscription(currentSubscription) && !!currentSubscription}
              isBasicActive={onBasicPlan
                || (!isLiveSubscription(currentSubscription) && !!currentSubscription)}
              // A downgrade to Basic is queued: the paid plan runs to this date,
              // then Basic starts. Same date the Cancellation scheduled banner
              // above prints, so the two cannot disagree.
              startsOn={cancellationBanner.visible ? cancellationBanner.accessUntil : null}
              disabled={processing || redirecting}
              // Basic is free on either interval. This only keeps its price row
              // reading the same way as the three beside it when the toggle moves
              // — "/Month" here under a row of "/Year" looks like a stale card.
              interval={billingInterval}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Topping up mid-month, for a PAID account ────────────────────────
          The Basic card carries "Buy credit pack" for a trial or a Basic
          account, which is where the request wants it. This is the other
          audience the link was added for: someone on a paid plan who has run
          out mid-month and needs credits, not a bigger tier.

          Without it that user's only route back to packs is the Credits page —
          the screen they navigated AWAY from to get here, which is the dead end
          the link originally fixed. It stays a text link, not a button: the
          Basic card's green CTA is the pay-as-you-go call to action, and a
          second filled button here would compete with it. */}
      {onBuyCreditPacks && isLiveSubscription(currentSubscription) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Typography
            variant="body2"
            sx={{ color: '#5A5F55', display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            {creditPacksPrompt(currentSubscription).question}
            <Box
              component="button"
              type="button"
              // Wrapped rather than passed straight to onClick, so the opener
              // is called with nothing instead of a click event. It ignores
              // its arguments today; being handed one is how that stops being
              // true silently.
              onClick={() => onBuyCreditPacks()}
              // Disabled rather than hidden before the packages arrive. A link
              // that appears late reads as a glitch; one visibly not ready does
              // not.
              disabled={!creditPacksReady}
              sx={{
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                font: 'inherit',
                fontWeight: 700,
                color: creditPacksReady ? '#1C7C73' : '#8A8F85',
                cursor: creditPacksReady ? 'pointer' : 'not-allowed',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                '&:hover': creditPacksReady ? { color: '#156874' } : {},
              }}
            >
              {creditPacksPrompt(currentSubscription).action}
            </Box>
          </Typography>
        </Box>
      )}

      {/* ── Confirmation dialog: quote, payment method, card entry, submit ── */}
      <Dialog
        open={confirmDialogOpen}
        onClose={processing ? undefined : handleCancelDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="confirm-plan-change-title"
      >
        <DialogTitle id="confirm-plan-change-title">
          {/* Names the direction, not just the destination. "Change to Scale"
              read the same for an upgrade and a downgrade, which are very
              different commitments. */}
          {/* The direction comes from the QUOTE, not from `can_upgrade`. That
              flag is set per card against the current plan and was only ever
              accurate enough to say "not an upgrade", which is why a downgrade
              was announced with the limp "Change to the Grow plan". The preview
              knows what it actually is. */}
          {/* A trial or Basic account is STARTING a subscription: the card it
              clicked said Subscribe, and the no-card path's checkout preamble
              already heads itself "Subscribe to the … plan". "Upgrade to the"
              named a plan the account does not have — and a Basic account's
              can_upgrade, priced against its ENDED plan, could even make it
              "Change to the".
              OLD: {selectedPlan
                ? `${scheduledDowngrade ? 'Downgrade to the' : (selectedPlan.can_upgrade === false ? 'Change to the' : 'Upgrade to the')} ${toDisplayName(selectedPlan)} plan`
                : 'Confirm Change'} */}
          {selectedPlan
            ? (hasPaidSubscription
              ? `${scheduledDowngrade ? 'Downgrade to the' : (selectedPlan.can_upgrade === false ? 'Change to the' : 'Upgrade to the')} ${toDisplayName(selectedPlan)} plan`
              : `Subscribe to the ${toDisplayName(selectedPlan)} plan`)
            : 'Confirm Change'}
        </DialogTitle>
        <DialogContent dividers>
          <ProrationSummary
            preview={preview}
            loading={previewLoading}
            error={previewError}
            selectedPlan={selectedPlan}
            currentPlan={currentPlan}
            onRetry={() => selectedPlan && loadProration(selectedPlan)}
          />

          {/* Payment method is only relevant when money changes hands today or
              at the next renewal on a new plan. A scheduled downgrade charges
              nothing now, and the existing card keeps working, so asking about
              cards there is noise. */}
          {quoteReady && preview?.effective !== 'period_end' && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <PaymentMethodChooser
                savedNotice={
                  'Your card is saved by Stripe for automatic renewals. '
                  + 'Remove it any time in Settings.'
                }
                methods={paymentMethods}
                loading={pmLoading}
                selectedPmId={selectedPmId}
                onSelect={(id) => { setSelectedPmId(id); setCardError(null); }}
                inlineAvailable={inlineCardsAvailable}
                clientSecret={setupClientSecret}
                cardRef={cardRef}
                onCardValidityChange={setCardComplete}
                error={cardError}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelDialog} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmChange}
            variant="contained"
            // A first subscription is never a warning: on a Basic account that
            // used to subscribe, can_upgrade is priced against the ENDED plan.
            // OLD: color={selectedPlan?.can_upgrade ? 'primary' : 'warning'}
            color={(!hasPaidSubscription || selectedPlan?.can_upgrade) ? 'primary' : 'warning'}
            // Disabled until there is a quote AND a usable card. A confirm
            // button that can be pressed before either is known is how BILL-003
            // happened in the first place.
            disabled={!canConfirm}
          >
            {processing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Working…
              </>
            ) : confirmLabel()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subscription active</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your subscription is live and your credits have been added. A receipt is on its way to your email.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSuccessDialogClose}
            variant="contained"
            color="primary"
            fullWidth
          >
            {/* BILL-009 — "OK" everywhere; see CreditManagement for the note. */}
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Already scheduled ─────────────────────────────────────────────────
          Shown in place of the quote when the customer picks the plan their
          downgrade is already queued for. Not an error — nothing went wrong and
          nothing needs fixing — so it is a plain notice with one way out, and
          it deliberately does NOT sit in the error dialog above. */}
      <Dialog
        open={scheduledNoticeOpen}
        onClose={() => setScheduledNoticeOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="downgrade-scheduled-title"
      >
        <DialogTitle id="downgrade-scheduled-title">{DOWNGRADE_SCHEDULED_TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {/* The plan name and date come from the same helpers the pending
                banner above uses, so the notice and the banner cannot describe
                the same scheduled change differently. */}
            {scheduledDowngradeMessage(pendingChange, {
              planDisplayName: (name) => getPlanDisplayName({ name }),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduledNoticeOpen(false)} variant="contained" color="primary">
            {/* BILL-009 — "OK" everywhere; see CreditManagement for the note. */}
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic Error Dialog — reserved for faults the user cannot fix here.
          Anything card-related is handled inline so it is never a dead end. */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{errorDialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* No address, no purchase — collected here, and the plan change resumes
          on save rather than sending the user off to Settings. */}
      {/* ── What you are about to buy ─────────────────────────────────────
          Shown only on a FIRST subscription with no saved card, which is the
          one path that leaves for Stripe's hosted checkout. That page renders
          the shared Stripe Product — "Subscribe to ImproveSEO Subscription" —
          so without this, the last time the customer sees the plan they chose
          is the card they clicked. See checkoutPreamble for the wording and
          for why that page cannot be fixed from here.

          Cancel starts nothing: the checkout session is not created until
          Continue is pressed. */}
      {checkoutIntent && (() => {
        const copy = checkoutPreamble({
          planName: checkoutIntent.name,
          planSlug: checkoutIntent.slug,
          priceCents: checkoutIntent.price_cents,
          credits: checkoutIntent.content_credits,
          pieces: checkoutPieces,
        });
        return (
          <Dialog
            open
            onClose={() => { if (!redirecting) setCheckoutIntent(null); }}
            maxWidth="xs"
            fullWidth
            aria-labelledby="checkout-preamble-title"
            PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
          >
            <DialogTitle id="checkout-preamble-title" sx={{ fontWeight: 700, pb: 0.5 }}>
              {copy.title}
            </DialogTitle>
            <DialogContent>
              {copy.price && (
                <Typography sx={{ fontWeight: 700, fontSize: 24, mb: copy.detail ? 1 : 0 }}>
                  {copy.price}
                </Typography>
              )}
              {copy.detail && <DialogContentText>{copy.detail}</DialogContentText>}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
              <Button onClick={() => setCheckoutIntent(null)} disabled={redirecting} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={proceedToHostedCheckout}
                disabled={redirecting}
                variant="contained"
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              >
                {redirecting ? <CircularProgress size={18} /> : 'Continue to secure checkout'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

/**
 * The itemised, pre-confirmation disclosure. (BILL-003)
 *
 * Everything here comes from the provider's invoice preview. Nothing is
 * calculated in this file — if a number is not in the payload it is not shown,
 * because a figure we worked out ourselves is a figure that can disagree with
 * the invoice.
 */
export const ProrationSummary = ({ preview, loading, error, selectedPlan, currentPlan, onRetry }) => {
  const [showWorking, setShowWorking] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }} aria-live="polite">
        <CircularProgress size={18} />
        <Typography variant="body2">Working out exactly what you'll be charged…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={onRetry ? <Button size="small" onClick={onRetry}>Try again</Button> : undefined}
      >
        {error} You have not been charged, and nothing has changed.
      </Alert>
    );
  }

  if (!preview) return null;

  const currency = preview.currency || 'usd';
  const amountDue = Number(preview.amount_due_cents ?? 0);
  const outcome = describeProration(amountDue, currency);
  const scheduled = preview.effective === 'period_end';

  // ── A scheduled downgrade gets its own body ───────────────────────────────
  // The generic layout below is built around a payment: an amount heading, a
  // "see how this is calculated" breakdown, a "then X per month" line, a
  // separate effective-date sentence and an allowance note. A downgrade has no
  // payment, so all of that rendered as "Total due today / $0.00" followed by
  // four fragments the reader had to assemble themselves.
  //
  // Three paragraphs instead, built in utils/subscriptionCopy so the wording is
  // testable without mounting a dialog.
  if (scheduled) {
    const paragraphs = downgradeQuoteMessage({
      fromPlan: currentPlan?.name,
      toPlan: selectedPlan?.name,
      // The renewal date: when the new plan starts AND when the next payment is
      // taken. One date throughout, so the dialog cannot contradict itself.
      effectiveDate: preview.effective_date || preview.next_billing_date,
      currentCredits: preview.current_credits,
      nextAmountCents: preview.next_amount_cents ?? selectedPlan?.price_cents,
      newAllotment: preview.credits?.new_allotment,
      currency,
    });

    return (
      <Box>
        <RichMessage
          paragraphs={paragraphs}
          variant="body2"
          color="text.secondary"
        />
      </Box>
    );
  }

  const rows = buildQuoteBreakdown(preview.changes, {
    toPlan: selectedPlan?.name,
    fromPlan: currentPlan?.name,
    daysRemaining: preview.days_remaining,
    currency,
  });

  // Only on a prorated quote. A first subscription pays a full month against no
  // part-period at all, so there is nothing to explain and the line would read
  // as a caveat on a plain price.
  const basisNote = preview.prorated
    ? prorationBasisNote(preview.next_billing_date)
    : null;

  return (
    <Box>
      {/* What is happening, in one line. "active immediately" answers the
          question the old dialog left open — a downgrade is scheduled, an
          upgrade is not, and both used to read the same. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {currentPlan ? (
          <>
            {toDisplayName(currentPlan)} plan → <strong>{toDisplayName(selectedPlan)} plan</strong>
            {scheduled ? ', at the end of this period' : ', active immediately'}
          </>
        ) : (
          <>Starting the <strong>{toDisplayName(selectedPlan)}</strong> plan</>
        )}
      </Typography>

      {/* ── The amount, stated once and prominently ──────────────────────────
          It used to be the last line of a four-row table the customer had to
          add up. Now it leads, and the arithmetic is available underneath for
          anyone who wants it. */}
      <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {outcome.kind === 'credit'
          ? `${outcome.amount} account credit`
          : `${outcome.kind === 'none' ? formatMoney(0, currency) : outcome.amount} due today`}
      </Typography>

      {outcome.kind === 'credit' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          You'll receive {outcome.amount} in account credit, applied to your next invoice.
        </Typography>
      )}
      {outcome.kind === 'none' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          No charge today.
        </Typography>
      )}

      {/* Collapsed by default: most people want the figure, and the ones who
          want the derivation are the ones who will look for it. */}
      {rows.length > 0 && (
        <>
          <Box
            component="button"
            type="button"
            onClick={() => setShowWorking((v) => !v)}
            aria-expanded={showWorking}
            sx={{
              mt: 0.5, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 0.25,
              font: 'inherit', fontSize: 13, fontWeight: 600, color: 'primary.main',
            }}
          >
            {showWorking ? 'Hide details' : 'See how this is calculated'}
            <ExpandMoreIcon
              sx={{ fontSize: 18, transform: showWorking ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
            />
          </Box>

          <Collapse in={showWorking} unmountOnExit>
            <Box component="dl" sx={{ m: 0, mt: 1, mb: 0.5 }}>
              {rows.map((row) => (
                <Box
                  key={row.key}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
                >
                  <Typography component="dt" variant="body2" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography
                    component="dd"
                    variant="body2"
                    sx={{ m: 0, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    {row.amount}
                  </Typography>
                </Box>
              ))}

              {preview.tax_cents ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                  <Typography component="dt" variant="body2" color="text.secondary">Tax</Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                    {formatMoney(preview.tax_cents, currency)}
                  </Typography>
                </Box>
              ) : null}

              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                <Typography component="dt" variant="subtitle2">Total due today</Typography>
                <Typography
                  component="dd"
                  variant="subtitle2"
                  sx={{ m: 0, fontVariantNumeric: 'tabular-nums' }}
                >
                  {outcome.kind === 'none' ? formatMoney(0, currency) : outcome.amount}
                </Typography>
              </Box>

              {/* Why the amounts are not round. The rows above count whole
                  days; Stripe prorates to the second, so the two never quite
                  reconcile and the cents get blamed for it. See
                  utils/upgradeQuote — prorationBasisNote. */}
              {basisNote && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}
                >
                  {basisNote}
                </Typography>
              )}
            </Box>
          </Collapse>
        </>
      )}

      {/* The recurring charge that follows. */}
      {preview.next_billing_date && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Then {preview.next_amount_cents != null
            ? formatMoney(preview.next_amount_cents, currency)
            : formatMoney(selectedPlan?.price_cents, currency)}
          {' per month, starting '}
          {formatQuoteDate(preview.next_billing_date)}
        </Typography>
      )}

      {scheduled && preview.effective_date && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This change takes effect on {formatQuoteDate(preview.effective_date)}.
          Until then nothing about your plan changes.
        </Typography>
      )}

      <CreditSummary
        credits={preview.credits}
        selectedPlan={selectedPlan}
        currentPlan={currentPlan}
        daysRemaining={preview.days_remaining}
      />
    </Box>
  );
};

/**
 * The credit half of the disclosure. (BILL-003, extended)
 *
 * Renders nothing when the server sends no `credits` block — an older server,
 * or a change that alters nothing — so this degrades to the previous dialog
 * rather than inventing a figure. Nothing here is calculated: every number is
 * the one the grant will actually use.
 */
export const CreditSummary = ({ credits, selectedPlan, currentPlan, daysRemaining }) => {
  if (!credits) return null;

  const newAllotment = Number(credits.new_allotment ?? 0);
  const n = (v) => Number(v).toLocaleString();

  // A scheduled downgrade: nothing lands today, and saying when the allotment
  // changes stops "your allowance becomes 220" reading as an immediate cut.
  if (Number(credits.granted_now ?? 0) === 0 && credits.effective_date) {
    return (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Your monthly allowance becomes <strong>{n(newAllotment)} credits</strong> on{' '}
          {formatQuoteDate(credits.effective_date)}. The credits you have now are not
          affected before then.
        </Typography>
      </>
    );
  }

  const headline = creditHeadline(credits);
  if (!headline) return null;

  const explanation = creditExplanation(credits, {
    fromPlan: currentPlan?.name,
    toPlan: selectedPlan?.name,
    daysRemaining,
  });

  return (
    <>
      <Divider sx={{ my: 2 }} />

      {/* The grant and the balance it lands on, in one line. It used to be a
          "Credits added today  +570" row, which named the delta but never the
          number the customer would actually see afterwards. */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {headline}
      </Typography>

      {/* Why it is not the whole difference — asked before it is wondered. */}
      {explanation && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {explanation}
        </Typography>
      )}

      {newAllotment > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          You'll get <strong>{n(newAllotment)} credits</strong> at each renewal
          {credits.renews_on || credits.effective_date
            ? `, starting ${formatQuoteDate(credits.renews_on || credits.effective_date)}`
            : ''}
        </Typography>
      )}
    </>
  );
};

// PaymentMethodChooser moved to its own file so the credit-pack dialog can
// use it too — a saved-card chooser with inline entry is not specific to
// changing plans. Re-exported here because this was its public home, and
// PlanSelector.test.jsx and CreditManagement both import it from here.
export { PaymentMethodChooser };

export default PlanSelector;
